// FILE: src/components/admin/RecordDownloads.jsx
// A student's full record as a file made by the server (WP-7; MTO 11.1,
// matrix 5.6): GET /api/admin/users/:id/record.pdf | .csv | .json, saved
// under the server's name (student-record-<registration number>-<date>.<ext>)
// with its SHA-256 fingerprint (X-Record-SHA256) shown so a copy can be
// checked later. Each download is audited (RECORD_EXPORTED).
import React, { useState } from "react";
import { Alert, AlertTitle, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import DataObjectOutlinedIcon from "@mui/icons-material/DataObjectOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { downloadFromApi, formatBytes } from "./download";
import { errorMessage } from "../../i18n/errors";

export const RECORD_FORMATS = [
  { format: "pdf", label: "PDF", note: "To read or print", Icon: PictureAsPdfOutlinedIcon },
  { format: "csv", label: "CSV", note: "For a spreadsheet", Icon: TableChartOutlinedIcon },
  { format: "json", label: "JSON", note: "Data file", Icon: DataObjectOutlinedIcon }
];

/** The record JSON's `record` from a downloaded JSON file (null when it can't be read). */
async function recordFromBlob(blob) {
  try {
    const text = typeof blob?.text === "function" ? await blob.text() : null;
    return text ? JSON.parse(text)?.record || null : null;
  } catch {
    return null;
  }
}

/** onRecord(record) runs after a JSON download, so the page can show parts of it. */
export default function RecordDownloads({ userId, onRecord }) {
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const download = async (format) => {
    setBusy(format);
    setError(null);
    setCopied(false);
    try {
      const file = await downloadFromApi(`/api/admin/users/${encodeURIComponent(userId)}/record.${format}`, {
        fallbackName: `student-record.${format}`
      });
      setResult({ filename: file.filename, sha256: file.sha256, size: file.size, format });
      if (format === "json" && onRecord) {
        const record = await recordFromBlob(file.blob);
        if (record) onRecord(record);
      }
    } catch (err) {
      setResult(null);
      setError(err || "INTERNAL_ERROR");
    } finally {
      setBusy(null);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.sha256);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" } }}>
        {RECORD_FORMATS.map(({ format, label, note, Icon }, i) => (
          <Button
            key={format}
            variant={i === 0 ? "contained" : "outlined"}
            onClick={() => download(format)}
            disabled={!!busy}
            startIcon={busy === format ? <CircularProgress size={18} color="inherit" aria-hidden /> : <Icon />}
            sx={{ justifyContent: "flex-start", py: 1.25, textAlign: "left" }}
          >
            <Box component="span">
              <Box component="span" sx={{ display: "block" }}>
                {busy === format ? `Preparing the ${label}…` : `Download ${label}`}
              </Box>
              <Box component="span" sx={{ display: "block", fontWeight: 400 }}>
                {note}
              </Box>
            </Box>
          </Button>
        ))}
      </Box>

      {error && (
        <Alert severity="error" role="alert">
          <AlertTitle>We couldn't make the record</AlertTitle>
          {errorMessage(error)}
        </Alert>
      )}

      {result && (
        <Alert severity="success" role="status" data-testid="record-result">
          <AlertTitle>Record downloaded</AlertTitle>
          <Typography>
            Saved as <strong>{result.filename}</strong>
            {result.size != null ? ` (${formatBytes(result.size)})` : ""}.
          </Typography>
          {result.sha256 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography sx={{ fontWeight: 600 }}>SHA-256 fingerprint</Typography>
              <Typography
                component="code"
                data-testid="record-sha256"
                sx={{ display: "block", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", overflowWrap: "anywhere", my: 0.5 }}
              >
                {result.sha256}
              </Typography>
              <Button size="medium" startIcon={<ContentCopyOutlinedIcon />} onClick={copy} sx={{ px: 0 }}>
                {copied ? "Fingerprint copied" : "Copy fingerprint"}
              </Button>
            </Box>
          )}
        </Alert>
      )}
    </Stack>
  );
}
